import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';

import Button from 'views/site/Button.js';
import Icon from 'views/site/Icon.js';
import QuizCTA from 'views/site/QuizCTA.js';
import Request from 'files/Request.js';
import Section from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Reviews
 *
 * The page at ?route=testimonials. It replaces the legacy GoogleReviews panel
 * in managers/Testimonials.js, which drew with Appearance, a Lottie loader and
 * image stars, and reported a failed fetch with a modal alert. The home page's
 * short version of this is views/site/Testimonials.js; this is the full list,
 * with the dealership's replies and paging.
 *
 * Every review is Google's, unedited and untruncated unless the reader chooses
 * to collapse it. The API does not return the profile's total count or overall
 * rating, so the summary is only ever the average of what has been loaded, and
 * says so. Presenting a partial average as the whole would be the kind of
 * small overstatement a reviews page cannot afford.
 */

// Long enough that most reviews show whole; past it the card offers the rest.
const CLAMP_LENGTH = 420;

const STAR_COLOR = '#F5A623';

const formatDate = value => {
    if(!value) {
        return null;
    }
    let date = moment.utc(value).local();
    return date.isValid() ? date.format('MMMM D, YYYY') : null;
}

// Cut on a word boundary so a clamped review never ends mid-word.
const clampText = text => {
    let cut = text.slice(0, CLAMP_LENGTH);
    let space = cut.lastIndexOf(' ');
    return `${(space > CLAMP_LENGTH * 0.7 ? cut.slice(0, space) : cut).replace(/[\s,.;:]+$/, '')}…`;
}

const getInitials = name => {
    let parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if(parts.length === 0) {
        return null;
    }
    return parts.length === 1 ? parts[0].charAt(0).toUpperCase() : `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

const Stars = ({ rating, size = 13 }) => {
    let value = Math.max(0, Math.min(5, parseInt(rating) || 0));
    return (
        <div
        aria-label={`Rated ${value} out of 5`}
        role={'img'}
        style={{ display: 'flex', flexShrink: 0, gap: 2 }}>
            {[...Array(5)].map((item, index) => (
                <Icon
                color={index < value ? STAR_COLOR : Theme.colors.lighterGrey}
                key={index}
                name={'star-fill'}
                size={size} />
            ))}
        </div>
    )
}

const Avatar = ({ name, src }) => {

    // Google profile photos fail often enough — deleted accounts, referrer
    // checks — that a broken image is a normal case rather than an edge one.
    const [failed, setFailed] = useState(false);

    let initials = getInitials(name);
    if(src && failed === false) {
        return (
            <img
            alt={''}
            onError={setFailed.bind(this, true)}
            referrerPolicy={'no-referrer'}
            src={src}
            style={{
                borderRadius: '50%',
                flexShrink: 0,
                height: 40,
                objectFit: 'cover',
                width: 40
            }} />
        )
    }
    return (
        <div style={{
            ...Theme.textStyles.caption({ color: Theme.colors.grey, fontSize: 14, fontWeight: 600 }),
            alignItems: 'center',
            backgroundColor: Theme.colors.offWhite,
            borderRadius: '50%',
            display: 'flex',
            flexShrink: 0,
            height: 40,
            justifyContent: 'center',
            width: 40
        }}>
            {initials || <Icon name={'person-fill'} size={14} color={Theme.colors.lightGrey} />}
        </div>
    )
}

const ReviewCard = ({ dealership, index, review }) => {

    const mobile = Utils.isMobile();
    const [expanded, setExpanded] = useState(false);

    let text = String(review.text || '').trim();
    let clamped = text.length > CLAMP_LENGTH;
    let reply = review.reply?.text ? review.reply : null;

    return (
        <article
        className={'review-in'}
        style={{
            ...Theme.styles.card(),
            '--review-index': index,
            boxShadow: Theme.boxShadow.card,
            padding: mobile ? 22 : 28
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                gap: 12
            }}>
                <Avatar name={review.full_name} src={review.avatar} />
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <span style={{
                        ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 600, lineHeight: 1.35 }),
                        display: 'block'
                    }}>{review.full_name || 'Google user'}</span>
                    <span style={{
                        ...Theme.textStyles.caption(),
                        display: 'block'
                    }}>{review.date_formatted || 'Google review'}</span>
                </div>
                <Stars rating={review.rating} />
            </div>

            {text.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <span style={{
                        ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, lineHeight: 1.65 }),
                        display: 'block',
                        whiteSpace: 'pre-line'
                    }}>{clamped && expanded === false ? clampText(text) : text}</span>
                    {clamped && (
                        <button
                        aria-expanded={expanded}
                        className={'pressable'}
                        onClick={setExpanded.bind(this, !expanded)}
                        style={{
                            ...Theme.textStyles.body({ color: Theme.colors.primary(), fontSize: 14, fontWeight: 600 }),
                            backgroundColor: 'transparent',
                            marginTop: 8,
                            padding: 0
                        }}>{expanded ? 'Show less' : 'Read more'}</button>
                    )}
                </div>
            )}

            {/* The dealership's own answer, set back on the section grey so it
                reads as a response to the review rather than a second one */}
            {reply && (
                <div style={{
                    backgroundColor: Theme.colors.offWhite,
                    borderRadius: 12,
                    marginTop: 20,
                    padding: mobile ? '14px 16px' : '16px 20px'
                }}>
                    <div style={{
                        alignItems: 'baseline',
                        columnGap: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        marginBottom: 6
                    }}>
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 13, fontWeight: 600, lineHeight: 1.4 })}>
                            {`Response from ${dealership.name}`}
                        </span>
                        {reply.date_formatted && (
                            <span style={Theme.textStyles.caption({ fontSize: 12 })}>{reply.date_formatted}</span>
                        )}
                    </div>
                    <span style={{
                        ...Theme.textStyles.body({ fontSize: 14 }),
                        display: 'block',
                        whiteSpace: 'pre-line'
                    }}>{reply.text}</span>
                </div>
            )}
        </article>
    )
}

const Reviews = ({ dealership, onOpenBlindSpots, onOpenQuiz, utils }) => {

    const mobile = Utils.isMobile();

    // loading → ready | error. Paging is tracked apart from status so a failed
    // second page never takes down the reviews already on screen.
    const [status, setStatus] = useState('loading');
    const [paging, setPaging] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [reviews, setReviews] = useState([]);

    const enabled = dealership?.google_reviews?.enabled === true;
    const reviewUrl = dealership?.google_reviews?.new_review_url || null;

    // utils is rebuilt on every App render — pinning it keeps the fetch to one
    const utilsRef = useRef(utils);
    utilsRef.current = utils;

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const fetchPage = async token => {
        try {
            let { next_page_token, reviews: page } = await Request.get(utilsRef.current, '/dealerships/', {
                dealership_id: dealership.id,
                next_page_token: token || null,
                type: 'google_business_profile_reviews'
            });
            if(mountedRef.current === false) {
                return;
            }

            // Each review keeps its position within the page it arrived in, so
            // a newly loaded batch staggers in from its own first card rather
            // than from wherever the list had already reached. A first page
            // replaces the list, so a retry can never append a duplicate set.
            let mapped = (page || []).map((review, index) => ({
                ...review,
                batch_index: Math.min(index, 6),
                date_formatted: formatDate(review.date),
                ...review.reply && {
                    reply: {
                        ...review.reply,
                        date_formatted: formatDate(review.reply.date)
                    }
                }
            }));
            setReviews(local => token ? local.concat(mapped) : mapped);
            setNextPageToken(next_page_token || null);
            setPageError(null);
            setStatus('ready');

        } catch(e) {
            console.log(e.message);
            if(mountedRef.current === false) {
                return;
            }
            if(token) {
                setPageError('More reviews did not load. Please try again.');
            } else {
                setStatus('error');
            }
        } finally {
            if(mountedRef.current === true) {
                setPaging(false);
            }
        }
    }

    const onLoadMore = () => {
        setPaging(true);
        fetchPage(nextPageToken);
    }

    const onRetry = () => {
        setStatus('loading');
        fetchPage(null);
    }

    const onWriteReview = () => {
        window.open(reviewUrl, '_blank', 'noopener');
    }

    useEffect(() => {
        if(!dealership) {
            return;
        }
        if(enabled === false) {
            setStatus('ready');
            return;
        }
        fetchPage(null);
    }, [dealership?.id, enabled]);

    if(!dealership) {
        return null;
    }

    const getAverage = () => {
        let rated = reviews.filter(review => parseInt(review.rating) > 0);
        if(rated.length === 0) {
            return null;
        }
        let total = rated.reduce((sum, review) => sum + parseInt(review.rating), 0);
        return { count: rated.length, value: (total / rated.length).toFixed(1) };
    }

    const getMasthead = () => (
        <section style={{
            backgroundColor: Theme.colors.offWhite,
            borderBottom: `1px solid ${Theme.colors.border()}`,
            width: '100%'
        }}>
            <div style={{
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: mobile ? '40px 20px' : '64px 40px',
                textAlign: 'center'
            }}>
                <span style={{
                    ...Theme.textStyles.label(),
                    display: 'block',
                    marginBottom: 14
                }}>{'Reviews'}</span>
                <span style={{
                    ...Theme.textStyles.sectionHeading(),
                    display: 'block'
                }}>{'In their own words'}</span>
                <span style={{
                    ...Theme.textStyles.sectionSubheading(),
                    display: 'block',
                    margin: '16px auto 0',
                    maxWidth: 680
                }}>{`What families say about working with ${dealership.name}. Every review here comes straight from Google, unedited, so it is as easy for you to check as it is for us to show.`}</span>
            </div>
        </section>
    );

    const getWriteReview = () => {
        if(!reviewUrl) {
            return null;
        }
        return (
            <div style={{
                borderTop: `1px solid ${Theme.colors.divider()}`,
                marginTop: mobile ? 20 : 28,
                paddingTop: mobile ? 20 : 28
            }}>
                <span style={{
                    ...Theme.textStyles.subHeading({ fontSize: 17 }),
                    display: 'block',
                    marginBottom: 6
                }}>{'Worked with us?'}</span>
                <span style={{
                    ...Theme.textStyles.body({ fontSize: 14 }),
                    display: 'block',
                    marginBottom: 16
                }}>{'A few sentences about how it went helps the next family decide.'}</span>
                <Button
                icon={'google'}
                label={'Write a review'}
                onClick={onWriteReview}
                style={{ width: mobile ? '100%' : 'auto' }}
                type={'primary'} />
            </div>
        )
    }

    const getSummary = () => {

        let average = getAverage();
        if(!average) {
            return getWriteReview();
        }

        // Only claim "all" once there is no further page to fetch
        let caption = nextPageToken
            ? `Average of the ${average.count} reviews shown so far`
            : `Average across ${average.count === 1 ? 'the one Google review' : `all ${average.count} Google reviews`}`;

        return (
            <div>
                <div style={{
                    alignItems: mobile ? 'center' : 'flex-start',
                    display: 'flex',
                    flexDirection: mobile ? 'row' : 'column',
                    gap: mobile ? 16 : 10
                }}>
                    <span style={Theme.textStyles.stat()}>{average.value}</span>
                    <div>
                        <Stars rating={Math.round(parseFloat(average.value))} size={mobile ? 15 : 17} />
                        <span style={{
                            ...Theme.textStyles.caption(),
                            display: 'block',
                            marginTop: 6
                        }}>{caption}</span>
                    </div>
                </div>
                {getWriteReview()}
            </div>
        )
    }

    const getSkeleton = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[0, 1, 2].map(index => (
                <div
                key={index}
                style={{
                    ...Theme.styles.card(),
                    padding: mobile ? 22 : 28
                }}>
                    <div style={{ alignItems: 'center', display: 'flex', gap: 12 }}>
                        <div className={'skeleton'} style={{ borderRadius: '50%', height: 40, width: 40 }} />
                        <div style={{ flexGrow: 1 }}>
                            <div className={'skeleton'} style={{ height: 12, marginBottom: 8, width: 140 }} />
                            <div className={'skeleton'} style={{ height: 10, width: 90 }} />
                        </div>
                    </div>
                    <div className={'skeleton'} style={{ height: 12, marginTop: 20, width: '100%' }} />
                    <div className={'skeleton'} style={{ height: 12, marginTop: 10, width: '92%' }} />
                    <div className={'skeleton'} style={{ height: 12, marginTop: 10, width: index === 1 ? '48%' : '70%' }} />
                </div>
            ))}
        </div>
    );

    // No reviews, reviews switched off, or the first fetch failed. One shape
    // for all three, so the page never ends in a blank space under the header.
    const getNotice = ({ action, copy, icon, title }) => (
        <div style={{
            backgroundColor: Theme.colors.offWhite,
            borderRadius: 20,
            margin: '0 auto',
            maxWidth: 640,
            padding: mobile ? '48px 24px' : '56px 40px',
            textAlign: 'center'
        }}>
            <div style={{
                alignItems: 'center',
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: '50%',
                display: 'flex',
                height: 56,
                justifyContent: 'center',
                margin: '0 auto 18px',
                width: 56
            }}>
                <Icon name={icon} size={20} color={icon === 'star-fill' ? STAR_COLOR : Theme.colors.grey} />
            </div>
            <span style={{
                ...Theme.textStyles.heading({ fontSize: 24 }),
                display: 'block',
                marginBottom: 10
            }}>{title}</span>
            <span style={{
                ...Theme.textStyles.body(),
                display: 'block',
                margin: '0 auto',
                maxWidth: 460
            }}>{copy}</span>
            {action && (
                <div style={{ marginTop: 24 }}>{action}</div>
            )}
        </div>
    );

    const getContent = () => {

        if(status === 'error') {
            return getNotice({
                action: <Button icon={'arrow-repeat'} label={'Try again'} onClick={onRetry} type={'outline'} />,
                copy: 'Google did not answer when we asked for the latest reviews. That is usually brief.',
                icon: 'warning',
                title: 'Reviews did not load'
            });
        }

        if(status === 'ready' && reviews.length === 0) {
            return getNotice({
                action: reviewUrl
                    ? <Button icon={'google'} label={'Write the first review'} onClick={onWriteReview} type={'primary'} />
                    : <Button label={'Get in touch'} onClick={() => utils.navigation.set({ key: 'contact', view: 'contact' })} type={'outline'} />,
                copy: reviewUrl
                    ? `${dealership.name} does not have any Google reviews yet. If we have been out to your home, yours could be the first.`
                    : `Reviews for ${dealership.name} are not available here yet. If you would like to speak with a family we have worked with, ask us.`,
                icon: 'star-fill',
                title: 'No reviews yet'
            });
        }

        let loading = status === 'loading';

        return (
            <div style={{
                alignItems: 'start',
                display: 'grid',
                gap: mobile ? 32 : 64,
                gridTemplateColumns: mobile ? '1fr' : '280px 1fr'
            }}>
                {/* The summary stays in view beside a long list, so the way to
                    add a review is never more than a glance away */}
                <aside style={{
                    position: mobile ? 'static' : 'sticky',
                    top: Theme.layout.headerHeight() + 32
                }}>
                    {loading ? (
                        <div>
                            <div className={'skeleton'} style={{ height: 52, marginBottom: 14, width: 88 }} />
                            <div className={'skeleton'} style={{ height: 12, width: 180 }} />
                        </div>
                    ) : getSummary()}
                </aside>

                <div style={{ minWidth: 0 }}>
                    {loading ? getSkeleton() : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {reviews.map((review, index) => (
                                <ReviewCard
                                dealership={dealership}
                                index={review.batch_index}
                                key={index}
                                review={review} />
                            ))}
                        </div>
                    )}

                    {loading === false && (nextPageToken || pageError) && (
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            marginTop: mobile ? 28 : 36
                        }}>
                            {pageError && (
                                <span style={Theme.textStyles.caption({ color: Theme.colors.error, textAlign: 'center' })}>{pageError}</span>
                            )}
                            {nextPageToken && (
                                <Button
                                label={'Show more reviews'}
                                loading={paging}
                                onClick={onLoadMore}
                                style={{ width: mobile ? '100%' : 'auto' }}
                                type={'outline'} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div
        className={'site'}
        style={{
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            {getMasthead()}

            <Section id={'testimonials'}>
                {getContent()}
            </Section>

            <QuizCTA
            dealership={dealership}
            heading={'Ready to see what your home needs?'}
            onOpenQuiz={onOpenBlindSpots || onOpenQuiz}
            subtext={'It takes about a minute and ends with a list you can hand straight to us or change first.'}
            /* Last thing before the footer, which is dark. See About.js. */
            tone={'light'} />
        </div>
    )
}

export default Reviews;
